// Cookie consent state shared by the banner (components/CookieConsent.js),
// Google Consent Mode v2 (gtag in public/index.html) and the consent-gated
// trackers (components/ConsentTrackers.jsx, mounted by LazyTrackers).
//
// Nothing optional runs before the visitor chooses: index.html sets every
// Consent Mode signal to "denied" before GA is configured and downloads
// gtag.js only after analytics consent (window.anumLoadGa, called from
// ConsentTrackers), and the Meta, TikTok, Snap and Clarity scripts are
// only injected after consent.
// The storage key and the mapping below are mirrored in the inline script in
// public/index.html (returning visitors); keep the two in sync.

export const CONSENT_STORAGE_KEY = 'anum360_cookie_consent';
export const CONSENT_EXPIRY_DAYS = 365;
/** window event fired with `detail` = the new consent after every change. */
export const CONSENT_CHANGE_EVENT = 'anum360:consent-change';
/** window event that re-opens the banner (footer / privacy page link). */
export const OPEN_COOKIE_SETTINGS_EVENT = 'anum360:open-cookie-settings';

export const CONSENT_CATEGORIES = ['essential', 'analytics', 'marketing', 'personalisation'];

export const DEFAULT_CONSENT = Object.freeze({
  essential: true,
  analytics: false,
  marketing: false,
  personalisation: false,
});

const DAY_MS = 24 * 60 * 60 * 1000;

/** Coerce anything into a full consent object (unknown → denied, essential always on). */
export const normalizeConsent = (value) => {
  const src = value && typeof value === 'object' ? value : {};
  return {
    essential: true,
    analytics: src.analytics === true,
    marketing: src.marketing === true,
    personalisation: src.personalisation === true,
  };
};

/** The stored choice, or null when none / expired / unreadable. */
export const loadConsent = (now = Date.now()) => {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = Number(parsed?.ts);
    if (!parsed?.consent || !Number.isFinite(ts) || (now - ts) / DAY_MS > CONSENT_EXPIRY_DAYS) {
      window.localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }
    return normalizeConsent(parsed.consent);
  } catch {
    return null;
  }
};

/** Google Consent Mode v2 signals for a consent choice. */
export const consentModeState = (consent) => {
  const c = normalizeConsent(consent);
  const v = (on) => (on ? 'granted' : 'denied');
  return {
    analytics_storage: v(c.analytics),
    ad_storage: v(c.marketing),
    ad_user_data: v(c.marketing),
    ad_personalization: v(c.marketing),
    functionality_storage: v(c.personalisation),
    personalization_storage: v(c.personalisation),
  };
};

/** The consent in force now (defaults to everything optional denied). */
export const getConsent = () => {
  if (typeof window === 'undefined') return { ...DEFAULT_CONSENT };
  if (window.cookieConsent) return normalizeConsent(window.cookieConsent);
  return loadConsent() || { ...DEFAULT_CONSENT };
};

export const hasConsent = (category) => getConsent()[category] === true;

/** Apply a choice without storing it (used on start-up for a stored choice). */
export const applyConsent = (consent) => {
  const c = normalizeConsent(consent);
  if (typeof window === 'undefined') return c;
  window.cookieConsent = c;
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', consentModeState(c));
  }
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: c }));
  } catch { /* very old browsers: trackers re-check on next render */ }
  return c;
};

/** Store and apply a choice. */
export const saveConsent = (consent, now = Date.now()) => {
  const c = normalizeConsent(consent);
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({ consent: c, ts: now }));
  } catch { /* storage blocked: the choice still applies for this visit */ }
  return applyConsent(c);
};

/** Subscribe to consent changes; returns the unsubscribe function. */
export const onConsentChange = (callback) => {
  if (typeof window === 'undefined') return () => {};
  const handler = (event) => callback(normalizeConsent(event?.detail));
  window.addEventListener(CONSENT_CHANGE_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler);
};

/** Re-open the cookie banner so the visitor can change or withdraw consent. */
export const openCookieSettings = () => {
  try {
    window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
  } catch { /* non-browser */ }
};

/** Expire cookies by name on this host and its parent domains. */
export const deleteCookies = (names) => {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname || '';
  const parts = host.split('.');
  const domains = [''];
  for (let i = 0; i < parts.length - 1; i += 1) {
    domains.push(`; domain=.${parts.slice(i).join('.')}`);
  }
  const existing = document.cookie.split(';').map((c) => c.split('=')[0].trim()).filter(Boolean);
  const matches = (name) => names.some((n) => (n instanceof RegExp ? n.test(name) : n === name));
  existing.filter(matches).forEach((name) => {
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    });
  });
};
